import OpenXIcon from '../assets/images/openx_icon.png';
import PubmaticIcon from '../assets/images/pubmatic_icon.ico';
import TTDIcon from '../assets/images/ttd_icon.png';
import axios from 'axios';
import {
  AUDIENCE_EXPORT_TYPES,
  ENUM_REGEX,
  ENUM_SYSTEM_USED,
  EXPERIAN_MAX_CHILD_SEGMENT_NAME_LENGTH,
  MAX_SEGMENT_DESCRIPTION_LENGTH,
  MAX_SEGMENT_NAME_LENGTH,
  OPENX_MAX_SEGMENT_NAME_LENGTH,
  TIKTOK_MAX_SEGMENT_NAME_LENGTH,
  TTD_DATA_ALLIANCE_MAX_SEGMENT_NAME_LENGTH,
  META_MAX_SEGMENT_NAME_LENGTH,
  META_MAX_DESCRIPTION_LENGTH,
  ValidationErrorMessages,
  YAHOO_DSP_CPM_MAX_VALUE,
  YAHOO_DSP_MAX_SEGMENT_NAME_LENGTH,
  LIVERAMP_MAX_SEGMENT_NAME_LENGTH,
  doesNotUseIs,
} from '../constants';
import { AUDIENCE_STUDIO_API_ENDPOINT } from '../pages/audienceManager/variables/index';
import { CSV_FILE_FORMAT_ERROR, VALID_FILE } from '../content';
import { auth } from 'utils/firebase';

/**
 * Get the epoch time (milliseconds since January 1, 1970, 00:00:00 UTC) for a given date.
 * @param {Date | string | number} date The date for which to calculate the epoch time.
 * @param {number} [hours=0] The hours component of the time (0-23).
 * @param {number} [minutes=0] The minutes component of the time (0-59).
 * @param {number} [seconds=0] The seconds component of the time (0-59).
 * @param {number} [milliseconds=0] The milliseconds component of the time (0-999).
 * @returns {number} The epoch time in milliseconds.
 */
export const getEpochTime = (
  date: Date | string | number,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0
): number => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  // Set hours, minutes, seconds, and milliseconds to provided values
  date.setUTCHours(hours, minutes, seconds, milliseconds);

  // Calculate epoch time
  return date.getTime();
};

/**
 * Handles onKeyDown event for decimal input fields.
 * Prevents non-numeric characters (except decimal point) from being entered.
 *
 * @param {React.KeyboardEvent} e - The keyboard event
 * @param {string} currentValue - The current value of the input field
 * @param {ENUM_REGEX} regex - The regex pattern to validate against (e.g., ENUM_REGEX.DECIMAL)
 * @returns {void}
 */

/** Final value: `-1` or a non-negative integer (no other negatives). */
const NEXXEN_DESTINATION_ID_PATTERN = /^(-1|\d+)$/;

/** Valid while typing: `""`, `"-"`, digit-only strings, or `"-1"` (see `NEXXEN_DESTINATION_ID_PATTERN`). */
const isValidNexxenDestinationPrefix = (s: string): boolean =>
  s === '' ||
  s === '-' ||
  s === '-1' ||
  /^\d+$/.test(s);

const validateNexxenDestinationId = (
  value: string,
  invalidMessage: string
): { isValid: boolean; message: string } => {
  const t = value.trim();
  if (t === '') {
    return { isValid: true, message: '' };
  }
  if (NEXXEN_DESTINATION_ID_PATTERN.test(t)) {
    return { isValid: true, message: '' };
  }
  return { isValid: false, message: invalidMessage };
};

export const validateNexxenAdvertiserDestinationId = (value: string) =>
  validateNexxenDestinationId(
    value,
    ValidationErrorMessages.NEXXEN_ADVERTISER_ID_INTEGER_ERROR
  );

export const validateNexxenMarketDestinationId = (value: string) =>
  validateNexxenDestinationId(
    value,
    ValidationErrorMessages.NEXXEN_MARKET_ID_INTEGER_ERROR
  );

export const handleDecimalKeyDown = (
  e: React.KeyboardEvent,
  currentValue: string,
  regex: ENUM_REGEX = ENUM_REGEX.DECIMAL
): void => {
  // Allow paste operations: Control+V (Windows/Linux) and Cmd+V (Mac)
  if ((e.ctrlKey && e.key === 'v') || (e.metaKey && e.key === 'v')) {
    return;
  }

  if (regex === ENUM_REGEX.NEXXEN_WHOLE_OR_MINUS_ONE) {
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    // Navigation and non-printable keys use key names longer than one character.
    if (e.key.length !== 1) {
      return;
    }
    const next = currentValue + e.key;
    if (!isValidNexxenDestinationPrefix(next)) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }

  // Prevent spaces for decimal fields
  if (
    e.key === ' ' ||
    e.key === 'Space' ||
    e.keyCode === 32 ||
    e.code === 'Space'
  ) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  try {
    const regexObj = new RegExp(regex);
    if (
      e.key.length === 1 &&
      !regexObj.test(currentValue + e.key) &&
      ![
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Enter',
      ].includes(e.key)
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  } catch (err) {
    console.error('Invalid regex pattern:', regex, err);
  }
};

/**
 * Handles onInput event for decimal input fields.
 * Sanitizes the input to only allow valid decimal values.
 *
 * @param {React.FormEvent} e - The input event
 * @param {string} currentValue - The current value of the input field
 * @param {(value: string) => void} onChange - Callback function to update the value
 * @param {ENUM_REGEX} regex - The regex pattern to validate against (e.g., ENUM_REGEX.DECIMAL)
 * @returns {void}
 */
export const handleDecimalInput = (
  e: React.FormEvent,
  currentValue: string,
  onChange: (value: string) => void,
  regex: ENUM_REGEX = ENUM_REGEX.DECIMAL
): void => {
  const target = e.target as HTMLInputElement;
  const inputValue = target.value;

  // Revert to previous value if spaces are detected
  if (inputValue.includes(' ')) {
    target.value = currentValue;
    onChange(currentValue);
    return;
  }

  // Sanitize input for decimal fields
  if (regex === ENUM_REGEX.DECIMAL) {
    let sanitizedValue = inputValue.replace(/\s/g, '');
    const parts = sanitizedValue.split('.');
    if (parts.length > 2) {
      sanitizedValue = parts[0] + '.' + parts.slice(1).join('');
    }
    sanitizedValue = sanitizedValue.replace(/[^\d.]/g, '');
    if (inputValue !== sanitizedValue) {
      target.value = sanitizedValue;
      onChange(sanitizedValue);
    }
  }
  // Sanitize input for whole number fields
  else if (regex === ENUM_REGEX.WHOLE_NUMBER) {
    const sanitizedValue = inputValue.replace(/[^\d]/g, '');
    if (inputValue !== sanitizedValue) {
      target.value = sanitizedValue;
      onChange(sanitizedValue);
    }
  } else if (regex === ENUM_REGEX.NEXXEN_WHOLE_OR_MINUS_ONE) {
    let v = inputValue.replace(/\s/g, '').replace(/[^\d-]/g, '');
    if (!v.startsWith('-')) {
      const sanitizedValue = v.replace(/[^\d]/g, '');
      if (inputValue !== sanitizedValue) {
        target.value = sanitizedValue;
        onChange(sanitizedValue);
      }
      return;
    }
    if (v === '-') {
      if (target.value !== '-') {
        target.value = '-';
        onChange('-');
      }
      return;
    }
    if (v.startsWith('-1')) {
      if (target.value !== '-1') {
        target.value = '-1';
        onChange('-1');
      }
      return;
    }
    if (target.value !== '-') {
      target.value = '-';
      onChange('-');
    }
  }
};

/**
 * Handles onBeforeInput event for decimal/whole number fields.
 * Prevents spaces from being entered.
 *
 * @param {React.FormEvent} e - The before input event
 * @param {ENUM_REGEX} regex - The regex pattern to validate against
 * @returns {void}
 */
export const handleDecimalBeforeInput = (
  e: React.FormEvent,
  regex?: ENUM_REGEX
): void => {
  if (
    regex &&
    [
      ENUM_REGEX.DECIMAL,
      ENUM_REGEX.WHOLE_NUMBER,
      ENUM_REGEX.TWO_DECIMAL_PLACES,
      ENUM_REGEX.NEXXEN_WHOLE_OR_MINUS_ONE,
    ].includes(regex)
  ) {
    const inputData = (e as any).data;
    if (inputData && inputData.includes(' ')) {
      e.preventDefault();
    }
  }
};

export const isFloat = (input: string) => {
  if (input === '') return false;
  // Regular expression to match float numbers (requires at least one digit)
  const floatRegex = /^-?(\d+\.?\d*|\d*\.\d+)$/;
  // Check if the input matches the float regex
  return floatRegex.test(input);
};

export const validateCustomAudienceCsv = (
  file: File,
  setValidationDetails: any
) => {
  const reader = new window.FileReader();

  reader.onload = (e) => {
    const contents = e?.target?.result;
    const lines = contents?.split('\n');

    // Check if CSV has only one column and all values are alphanumeric
    const isValid = lines.every((line) => {
      const values = line.trim().split(',');
      if (
        values.length === 1 &&
        ((values[0].match(/^[a-zA-Z0-9]+$/) && values[0].length === 40) ||
          values[0] === '')
      ) {
        return true;
      }
      return false;
    });

    if (isValid) {
      setValidationDetails({
        valid: true,
        message: VALID_FILE,
      });
    } else {
      setValidationDetails({
        valid: false,
        message: CSV_FILE_FORMAT_ERROR,
      });
    }
  };

  reader.readAsText(file);
};

/**
 * Retrieves audience emails.
 *
 * @param {boolean} isCustomAudience - Indicates if the audience is custom audience.
 * @returns {Promise<AxiosResponse>} A promise that resolves to the Axios response containing the audience emails.
 */
export const getAudeinceEmails = (isCustomAudience: boolean) => {
  const url = `${AUDIENCE_STUDIO_API_ENDPOINT}/emails`;
  return auth?.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(url);
  });
};

/**
 * Retrieves all modeled audiences.
 *
 * @returns {Promise<AxiosResponse>} A promise that resolves to the Axios response containing the all modeled audiences.
 */
export const getModeledAudeinces = () => {
  const url = `${AUDIENCE_STUDIO_API_ENDPOINT}/audiences/modeled`;
  return auth?.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(url);
  });
};

/**
 * Retrieves all modeled audience counts.
 *
 * @returns {Promise<AxiosResponse>} A promise that resolves to the Axios response containing the modeled audience counts.
 */
export const getModeledAudeinceCounts = (payload: any) => {
  const url = `${AUDIENCE_STUDIO_API_ENDPOINT}/audiences/modeledAudienceCount`;
  return auth?.currentUser?.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.post(url, payload);
  });
};

/**
 * Validates the segment name based on the selected export type.
 *
 * @param {string} selectedExportType - The type of export selected (e.g., 'custom_segment', 'ots_segment').
 * @param {string} segmentName - The name of the segment to be validated.
 * @returns {{ isValid: boolean, validationMessage: string }} - An object indicating whether the segment name is valid and a corresponding validation message.
 */
export const isSegmentNameValid = (
  selectedExportType: Array<string> = [],
  segmentName: string
) => {
  const returnObject = {
    isValid: true,
    validationMessage: '',
  };

  // Regular expression to detect forbidden characters (double quotes, single quote, caret)
  const forbiddenCharsPattern = /['"^]/;
  const experianForbiddenCharsPattern = /["\\]|\/{2}|[^\x00-\x7F]/;
  // Regular expression to validate A- pattern (A- followed by exactly 7 digits, then any characters allowed)
  const metaSegmentNameRegexPattern = /^.*A-\d{7}(?!\d).*$/;

  // Check for A- pattern validation for Meta exports (both seed and modeled)
  if (
    (selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) ||
      selectedExportType.includes(AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT)) &&
    segmentName &&
    !metaSegmentNameRegexPattern.test(segmentName)
  ) {
    returnObject.isValid = false;
    returnObject.validationMessage =
      ValidationErrorMessages.SEGMENT_NAME_A_PATTERN_ERROR;
    return returnObject;
  }

  // Meta name length: <= 121 characters (API limit 128; we append " (seed)" for seed exports)
  if (
    (selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) ||
      selectedExportType.includes(AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT)) &&
    segmentName &&
    segmentName.trim().length > META_MAX_SEGMENT_NAME_LENGTH
  ) {
    returnObject.isValid = false;
    returnObject.validationMessage =
      ValidationErrorMessages.META_SEGMENT_NAME_LENGTH_ERROR;
    return returnObject;
  }

  if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT)) {
    // Check for forbidden characters
    if (experianForbiddenCharsPattern.test(segmentName)) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.EXPERIAN_FORBIDDEN_CHARACTER_ERROR;
      return returnObject;
    }

    // Split the segment name by '>' and check the length of each part
    const segments = segmentName.split('>');

    for (const segment of segments) {
      if (segment.trim().length > EXPERIAN_MAX_CHILD_SEGMENT_NAME_LENGTH) {
        returnObject.isValid = false;
        returnObject.validationMessage =
          ValidationErrorMessages.EXPERIAN_SEGMENT_SECTION_TOO_LONG_ERROR;
        return returnObject;
      }
    }
  }
  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.OTS_SEGMENT) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
    ) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT)
  ) {
    // Check for forbidden characters
    if (forbiddenCharsPattern.test(segmentName)) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.FORBIDDEN_CHARACTER_ERROR;
      return returnObject;
    }

    if (segmentName && !segmentName.startsWith('Attain >')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_NAME_START_CHAR_ERROR;
      return returnObject;
    }

    // Check for consecutive '>' symbols
    if (segmentName.replace(/\s/g, '').includes('>>')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.CONSECUTIVE_GREATER_THAN_SYMBOL_ERROR;
      return returnObject;
    }

    // LiveRamp segment name total length must be <= 255 characters
    const isLiveRampExportType =
      selectedExportType.includes(
        AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
      ) ||
      selectedExportType.includes(
        AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
      ) ||
      selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT) ||
      selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT);
    if (
      isLiveRampExportType &&
      segmentName.trim().length > LIVERAMP_MAX_SEGMENT_NAME_LENGTH
    ) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.LIVERAMP_SEGMENT_NAME_LENGTH_ERROR;
      return returnObject;
    }

    // Split the segment name by '>' and check the length of each part
    const segments = segmentName.split('>');

    for (const segment of segments) {
      if (segment.trim().length > MAX_SEGMENT_NAME_LENGTH) {
        returnObject.isValid = false;
        returnObject.validationMessage =
          ValidationErrorMessages.SEGMENT_SECTION_TOO_LONG_ERROR;
        return returnObject;
      }
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT)
  ) {
    if (segmentName.trim().length > MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_SECTION_SHOULD_NOT_EXCEED_ERROR.replace(
          '${MAX_SEGMENT_NAME_LENGTH}',
          MAX_SEGMENT_NAME_LENGTH.toString()
        );
      return returnObject;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT)
  ) {
    if (segmentName.trim().length > OPENX_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_SECTION_SHOULD_NOT_EXCEED_ERROR.replace(
          '${MAX_SEGMENT_NAME_LENGTH}',
          OPENX_MAX_SEGMENT_NAME_LENGTH.toString()
        );
      return returnObject;
    }
  }

  if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.TTD_DATA_ALLIANCE)) {
    // Check for forbidden characters
    if (forbiddenCharsPattern.test(segmentName)) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.FORBIDDEN_CHARACTER_ERROR;
      return returnObject;
    }

    if (segmentName && !segmentName.startsWith('Data Alliance >')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.TTD_DATA_ALLIANCE_SEGMENT_NAME_START_CHAR_ERROR;
      return returnObject;
    }

    // Check for consecutive '>' symbols
    if (segmentName.replace(/\s/g, '').includes('>>')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.CONSECUTIVE_GREATER_THAN_SYMBOL_ERROR;
      return returnObject;
    }

    if (segmentName.trim().length > TTD_DATA_ALLIANCE_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.SEGMENT_SECTION_SHOULD_NOT_EXCEED_ERROR.replace(
          '${MAX_SEGMENT_NAME_LENGTH}',
          TTD_DATA_ALLIANCE_MAX_SEGMENT_NAME_LENGTH.toString()
        );
      return returnObject;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT)
  ) {
    // AP-5340: Additional validation — Custom must start with "Custom > ", OTS with "Syndicated > " (existing length/other checks unchanged)
    const trimmed = segmentName?.trim() ?? '';
    if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT) && trimmed && !trimmed.startsWith('Custom > ')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.YAHOO_DSP_CUSTOM_SEGMENT_PREFIX;
      return returnObject;
    }
    if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT) && trimmed && !trimmed.startsWith('Syndicated > ')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.YAHOO_DSP_OTS_SEGMENT_PREFIX;
      return returnObject;
    }
    if (trimmed.length > YAHOO_DSP_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.YAHOO_DSP_SEGMENT_NAME_LENGTH_ERROR;
      return returnObject;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE
    )
  ) {
    if (segmentName.trim().length > TIKTOK_MAX_SEGMENT_NAME_LENGTH) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.TIKTOK_SEGMENT_NAME_LENGTH_ERROR;
      return returnObject;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT)
  ) {
    const trimmedSegmentName = segmentName.trim();
    if (trimmedSegmentName.length < 10 || trimmedSegmentName.length > 128) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        'Segment name must be between 10 and 128 alphanumeric characters';
      return returnObject;
    }
    // Check if alphanumeric (letters, numbers, and spaces - A-Z, a-z, 0-9, space)
    if (!/^[a-zA-Z0-9\s]+$/.test(trimmedSegmentName)) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        'Segment name must be alphanumeric (letters and numbers only)';
      return returnObject;
    }
  }

  // Nexxen validation
  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT)
  ) {
    // Check if segment name starts with "Custom Segment >"
    if (!segmentName.trim().startsWith('Attain >')) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.NEXXEN_SEGMENT_START_ERROR;
      return returnObject;
    }

    // Check if each part is <= 50 characters
    const parts = segmentName.split('>').map((part) => part.trim());
    const invalidPart = parts.find((part) => part.length > 50);
    if (invalidPart) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.NEXXEN_SEGMENT_PART_LENGTH_ERROR;
      return returnObject;
    }
  }

  return returnObject;
};

export const isCPMValid = (
  selectedExportType: Array<string> = [],
  cpm: string
) => {
  const returnObject = {
    isValid: true,
    cpmErrorMessage: '',
  };

  // LiveRamp validation: CPM must be >= 0.01
  const isLiveRampType =
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
    ) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
    );

  if (isLiveRampType) {
    if (cpm !== '' && isFloat(cpm)) {
      const cpmValue = parseFloat(cpm);
      if (cpmValue <= 0 || cpmValue < 0.01) {
        returnObject.isValid = false;
        returnObject.cpmErrorMessage =
          ValidationErrorMessages.LIVERAMP_CPM_MIN_ERROR;
      }
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT)
  ) {
    if (!isFloat(cpm)) {
      returnObject.isValid = false;
      returnObject.cpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_CPM_RANGE_ERROR;
    }

    if (
      (cpm !== '' && parseFloat(cpm) < 0) ||
      parseFloat(cpm) > YAHOO_DSP_CPM_MAX_VALUE
    ) {
      returnObject.isValid = false;
      returnObject.cpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_CPM_RANGE_ERROR;
    }
  }

  // Amazon validation: CPM must be greater than 0
  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT)
  ) {
    if (cpm !== '' && isFloat(cpm)) {
      const cpmValue = parseFloat(cpm);
      if (cpmValue <= 0) {
        returnObject.isValid = false;
        returnObject.cpmErrorMessage =
          ValidationErrorMessages.AMAZON_CPM_INVALID;
      }
    }
  }

  return returnObject;
};

/**
 * Validates Programmatic % of media value for LiveRamp exports.
 * Must be greater than zero (>= 0.01).
 *
 * @param {Array<string>} selectedExportType - Array of selected export types
 * @param {string} percentageOfMedia - The percentage of media value to validate
 * @returns {{ isValid: boolean, percentageOfMediaErrorMessage: string }} - An object indicating whether the percentage of media is valid and a corresponding validation message.
 */
export const isPercentageOfMediaValid = (
  selectedExportType: Array<string> = [],
  percentageOfMedia: string
) => {
  const returnObject = {
    isValid: true,
    percentageOfMediaErrorMessage: '',
  };

  const isLiveRampType =
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
    ) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
    );

  if (isLiveRampType && percentageOfMedia !== '') {
    if (!isFloat(percentageOfMedia)) {
      returnObject.isValid = false;
      returnObject.percentageOfMediaErrorMessage =
        ValidationErrorMessages.LIVERAMP_PERCENTAGE_OF_MEDIA_MIN_ERROR;
    } else {
      const pomValue = parseFloat(percentageOfMedia);
      if (pomValue <= 0 || pomValue < 0.01) {
        returnObject.isValid = false;
        returnObject.percentageOfMediaErrorMessage =
          ValidationErrorMessages.LIVERAMP_PERCENTAGE_OF_MEDIA_MIN_ERROR;
      }
    }
  }

  return returnObject;
};

/**
 * Validates Advertiser Direct % of Media value for LiveRamp exports.
 * Must be greater than zero (>= 0.01).
 *
 * @param {Array<string>} selectedExportType - Array of selected export types
 * @param {string} advertiserDirect - The advertiser direct value to validate
 * @returns {{ isValid: boolean, advertiserDirectErrorMessage: string }} - An object indicating whether the advertiser direct is valid and a corresponding validation message.
 */
export const isAdvertiserDirectValid = (
  selectedExportType: Array<string> = [],
  advertiserDirect: string
) => {
  const returnObject = {
    isValid: true,
    advertiserDirectErrorMessage: '',
  };

  const isLiveRampType =
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT
    ) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE
    );

  if (isLiveRampType && advertiserDirect !== '') {
    if (!isFloat(advertiserDirect)) {
      returnObject.isValid = false;
      returnObject.advertiserDirectErrorMessage =
        ValidationErrorMessages.LIVERAMP_ADVERTISER_DIRECT_MIN_ERROR;
    } else {
      const adValue = parseFloat(advertiserDirect);
      if (adValue <= 0 || adValue < 0.01) {
        returnObject.isValid = false;
        returnObject.advertiserDirectErrorMessage =
          ValidationErrorMessages.LIVERAMP_ADVERTISER_DIRECT_MIN_ERROR;
      }
    }
  }

  return returnObject;
};

/**
 * Validates TTD (The Trade Desk) Partner ID or Advertiser ID.
 * IDs must contain only alphanumeric characters (no spaces or special characters).
 *
 * @param {string} id - The Partner ID or Advertiser ID to validate.
 * @returns {{ isValid: boolean, ttdIdErrorMessage: string }} - An object indicating whether the ID is valid and a corresponding validation message.
 */
export const isTTDIdValid = (id: string) => {
  const returnObject = {
    isValid: true,
    ttdIdErrorMessage: '',
  };

  // If ID is empty (null, undefined, or empty string), it's valid (required validation is handled separately)
  if (!id || id === '') {
    return returnObject;
  }

  // If ID contains only whitespace, it's invalid
  if (id.trim() === '') {
    returnObject.isValid = false;
    returnObject.ttdIdErrorMessage =
      ValidationErrorMessages.TTD_ID_ALPHANUMERIC_ERROR;
    return returnObject;
  }

  // Check if ID contains only alphanumeric characters (A-Z, a-z, 0-9)
  const alphanumericPattern = /^[a-zA-Z0-9]+$/;
  if (!alphanumericPattern.test(id)) {
    returnObject.isValid = false;
    returnObject.ttdIdErrorMessage =
      ValidationErrorMessages.TTD_ID_ALPHANUMERIC_ERROR;
  }

  return returnObject;
};

/**
 * Validates TV CPM value for Yahoo DSP exports.
 * TV CPM must be a number between 0 and 50.
 *
 * @param {string} tvCpm - The TV CPM value to validate.
 * @returns {{ isValid: boolean, tvCpmErrorMessage: string }} - An object indicating whether the TV CPM is valid and a corresponding validation message.
 */
export const isTvCpmValid = (
  selectedExportType: Array<string> = [],
  tvCpm: string
) => {
  const returnObject = {
    isValid: true,
    tvCpmErrorMessage: '',
  };

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT)
  ) {
    if (tvCpm === '' || !isFloat(tvCpm)) {
      returnObject.isValid = false;
      returnObject.tvCpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_TV_CPM_RANGE_ERROR;
      return returnObject;
    }

    const tvCpmValue = parseFloat(tvCpm);
    if (tvCpmValue < 0 || tvCpmValue > YAHOO_DSP_CPM_MAX_VALUE) {
      returnObject.isValid = false;
      returnObject.tvCpmErrorMessage =
        ValidationErrorMessages.YAHOO_DSP_TV_CPM_RANGE_ERROR;
      return returnObject;
    }
  }
  return returnObject;
};

/**
 * Returns the default operator for a given property name.
 *
 * Some property names use the '=' operator by default, while others use 'is'.
 * This function checks if the property name is in the list that uses '=' and
 * returns the appropriate default operator.
 *
 * @param {string} propertyName - The name of the property to check.
 * @returns {string} - The default operator for the given property ('=' or 'is').
 */
export const getDefaultOperator = (propertyName: string): string => {
  const defaultOperator = doesNotUseIs[propertyName] ? '=' : 'is';
  return defaultOperator;
};

export const sortTableData = (data: any, sortBy: string, sortOrder: string) => {
  return data.sort((a, b) => {
    let fieldA = a[sortBy];
    let fieldB = b[sortBy];
    const isNumber = !isNaN(Number(fieldA));
    // If the field is a string, compare them lexicographically
    if (!isNumber) {
      fieldA = fieldA?.toLowerCase();
      fieldB = fieldB?.toLowerCase();
    }

    // If sortOrder is ascending (ASC)
    if (sortOrder === 'ASC') {
      if (fieldA > fieldB) return 1;
      if (fieldA < fieldB) return -1;
      return 0;
    }
    // If sortOrder is descending (DESC)
    else if (sortOrder === 'DESC') {
      if (fieldA > fieldB) return -1;
      if (fieldA < fieldB) return 1;
      return 0;
    }

    return 0; // In case sortOrder is neither ASC nor DESC
  });
};

export const groupModeledAudienceCountByIdType = (data: any) => {
  if (!Array.isArray(data)) {
    return {};
  }
  const sortedData = [...data].sort((a, b) => a.idType.localeCompare(b.idType));

  return sortedData.reduce((acc: any, item: any) => {
    if (!acc[item.idType]) {
      acc[item.idType] = [];
    }
    acc[item.idType].push(item);
    return acc;
  }, {});
};

export const calculateMinAndMaxAudienceCount = (groupedData: any) => {
  let minAudienceCount = 0;
  let maxAudienceCount = 0;
  Object.values(groupedData).forEach((items) => {
    if (items[0]) {
      minAudienceCount += items[0].audienceCount;
    }
    if (items[10]) {
      maxAudienceCount += items[10].audienceCount;
    }
  });

  return {
    minimumAudienceCount: minAudienceCount.toLocaleString(),
    maximumAudienceCount: maxAudienceCount.toLocaleString(),
  };
};

/**
 * Check desciption props & validation
 */
export const isDescriptionValid = (
  selectedExportType: Array<string> = [],
  description: string
) => {
  const result = {
    isValid: true,
    isRequired: false,
    validationMessage: '',
  };

  // Check if Meta exports are selected - description is required for Meta exports (both seed and modeled)
  if (
    selectedExportType.includes(
      AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE
    ) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT)
  ) {
    result.isRequired = true;

    if (!description.trim()) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_REQUIRED_ERROR;
      return result;
    }

    if (description.trim().length > META_MAX_DESCRIPTION_LENGTH) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.META_DESCRIPTION_LENGTH_ERROR;
      return result;
    }
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT) ||
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT)
  ) {
    result.isRequired = true;

    if (!description.trim()) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_REQUIRED_ERROR;
      return result;
    }

    if (description.trim().length > MAX_SEGMENT_NAME_LENGTH) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_SECTION_TOO_LONG_ERROR;
    }

    return result;
  }

  if (selectedExportType.includes(AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT)) {
    const experianForbiddenCharsPattern = /["\\]|\/{2}|[^\x00-\x7F]/;
    result.isRequired = true;

    if (!description.trim()) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_REQUIRED_ERROR;
      return result;
    }

    // Check for forbidden characters
    if (experianForbiddenCharsPattern.test(description)) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.EXPERIAN_DESCRIPTION_NON_ASCII_CHARACTER_ERROR;
      return result;
    }

    if (description.trim().length > MAX_SEGMENT_DESCRIPTION_LENGTH) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.EXPERIAN_DESCRIPTION_TOO_LONG_ERROR;
    }

    return result;
  }

  if (
    selectedExportType.includes(AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT)
  ) {
    result.isRequired = true;

    const trimmedDescription = description.trim();

    // Check if description is empty
    if (!trimmedDescription) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.DESCRIPTION_REQUIRED_ERROR;
      return result;
    }

    // Check length (0-1000 characters)
    if (trimmedDescription.length > 1000) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.AMAZON_DESCRIPTION_LENGTH_ERROR;
      return result;
    }

    // Check if alphanumeric (letters, numbers, and spaces - A-Z, a-z, 0-9, space)
    if (!/^[a-zA-Z0-9\s]+$/.test(trimmedDescription)) {
      result.isValid = false;
      result.validationMessage =
        ValidationErrorMessages.AMAZON_DESCRIPTION_ALPHANUMERIC_ERROR;
      return result;
    }
  }

  return result;
};

export const generateOtsExportOptions = (audienceType: string) => {
  const otsOptions =
    audienceType === ENUM_SYSTEM_USED.COMBINED_AUDIENCE
      ? [
          { id: 'all', label: 'Toggle All' },
          {
            id: AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
            label: 'The Trade Desk',
            icon: TTDIcon,
          },
        ]
      : [
          { id: 'all', label: 'Toggle All' },
          {
            id: AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
            label: 'The Trade Desk',
            icon: TTDIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT,
            label: 'Pubmatic',
            icon: PubmaticIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT,
            label: 'OpenX',
            icon: OpenXIcon,
          },
        ];
  return otsOptions;
};

export const generatCustomExportOptions = (audienceType: string) => {
  const customOptions =
    audienceType === ENUM_SYSTEM_USED.COMBINED_AUDIENCE
      ? [
          {
            id: AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
            label: 'The Trade Desk Custom',
            icon: TTDIcon,
          },
        ]
      : [
          {
            id: AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
            label: 'The Trade Desk Custom',
            icon: TTDIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT,
            label: 'Pubmatic',
            icon: PubmaticIcon,
          },
          {
            id: AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT,
            label: 'OpenX',
            icon: OpenXIcon,
          },
        ];
  return customOptions;
};

export const sortRowsDescending = (rows: any) => {
  function extractNumberForSort(str: any) {
    if (!isNaN(str)) return parseInt(str, 10); // Entire string numeric
    const match = str.match(/(\d+)$/); // Numeric suffix for alphanumerics
    return match ? parseInt(match[1], 10) : NaN;
  }

  return rows.sort((a: any, b: any) => {
    const numA = extractNumberForSort(a.row);
    const numB = extractNumberForSort(b.row);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numB - numA; // Descending numeric order
    } else {
      return b.row.localeCompare(a.row); // Fallback to reverse lexicographic
    }
  });
};

/**
 * Validates Nexxen dates (start date and end date).
 * Start date is required and cannot be in the past.
 * End date (if provided) must be greater than or equal to start date.
 *
 * @param {string} startDate - The start date to validate (YYYY-MM-DD format)
 * @param {string} endDate - The end date to validate (YYYY-MM-DD format, optional)
 * @returns {{ isValid: boolean, validationMessage: string }} - An object indicating whether the dates are valid and a corresponding validation message.
 */
export const isNexxenDatesValid = (startDate: string, endDate?: string) => {
  const returnObject = {
    isValid: true,
    validationMessage: '',
  };

  // Check if start date is provided
  if (!startDate || startDate.trim() === '') {
    returnObject.isValid = false;
    returnObject.validationMessage =
      ValidationErrorMessages.NEXXEN_START_DATE_REQUIRED_ERROR;
    return returnObject;
  }

  // If end date is provided, validate it's after or equal to start date
  if (endDate && endDate.trim() !== '') {
    if (endDate < startDate) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.NEXXEN_END_DATE_INVALID_ERROR;
      return returnObject;
    }
  }

  return returnObject;
};

/**
 * Validates Nexxen currency rate
 * @param currencyRate - The currency rate value to validate
 * @returns Object with isValid boolean and validationMessage string
 */
export const isCurrencyRateValid = (currencyRate?: string) => {
  const returnObject = {
    isValid: true,
    validationMessage: '',
  };

  // Check if currencyRate is provided and not empty
  if (
    currencyRate !== undefined &&
    currencyRate !== null &&
    currencyRate.trim() !== ''
  ) {
    const currencyRateValue = parseFloat(currencyRate);

    // Check if it's a valid number and greater than 0
    if (isNaN(currencyRateValue) || currencyRateValue <= 0) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.NEXXEN_CURRENCY_RATE_POSITIVE_ERROR;
      return returnObject;
    }

    // Check if it exceeds the maximum allowed value
    if (currencyRateValue > 30) {
      returnObject.isValid = false;
      returnObject.validationMessage =
        ValidationErrorMessages.NEXXEN_CURRENCY_RATE_MAX_ERROR;
      return returnObject;
    }
  }

  return returnObject;
};
